import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { Typography } from '../../../widgets/Typography';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

interface DonutChartProps {
  data?: {
    backgroundColor?: string[];
    labels: string[];
    values: number[];
    percentages: number[];
  };
  className?: string;
}

export default function DonutChart({ data, className = '' }: DonutChartProps) {
  const chartData = {
    labels: data?.labels,
    datasets: [
      {
        data: data?.values,
        backgroundColor: data?.backgroundColor || [],
        borderColor: data?.backgroundColor || [],
        borderWidth: 0,
        hoverBorderWidth: 0,
        cutout: '25%', // Thinner ring
        rotation: -90, // Start from top
        circumference: 360, // Full circle
      },
    ],
  };

  const options: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // We'll create custom legend
      },
      tooltip: {
        enabled: true, // Disable default tooltip
        backgroundColor: '#fff', // White background
        titleColor: '#101828', // Dark text
        bodyColor: '#101828', // Dark text
        borderColor: 'rgba(16, 24, 40, 0.1)',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
        displayColors: true,
        usePointStyle: true,
        boxWidth: 8,
        boxHeight: 8,
        boxPadding: 6,
        titleFont: {
          size: 14,
          weight: 400,
        },
        bodyFont: {
          size: 14,
          weight: 400,
        },
        callbacks: {
          // Keep everything on one line; color dot precedes this text
          title: () => '',
          label: (context) => {
            const value = context.parsed;
            const percentage = data?.percentages[context.dataIndex];
            return `${context.label}: ${value} (${percentage}%)`;
          },
          labelPointStyle: () => ({
            pointStyle: 'circle',
            rotation: 0,
          }),
        },
      },
      datalabels: {
        display: false, // Hide datalabels if plugin is loaded
      },
    },
    elements: {
      arc: {
        borderRadius: 0, // Sharp edges for clean look
      },
    },
    layout: {
      padding: 0,
    },
  };

  return (
    <div
      className={`flex flex-col items-center justify-center gap-8 md:flex-row ${className}`}
    >
      {/* Chart Container */}
      <div className="relative h-70 w-70 shrink-0">
        <Doughnut
          data={chartData}
          options={options}
          className="h-full w-full"
        />
      </div>

      {/* Custom Legend */}
      <div className="relative flex shrink-0 flex-col items-start justify-start gap-1">
        {data?.labels.map((label, index) => (
          <div
            key={label}
            className="relative flex w-full shrink-0 items-center justify-start gap-2"
          >
            {/* Legend Dot - Circle matching design */}
            <div
              className="h-2 w-2 shrink-0 rounded-full"
              style={{
                backgroundColor: data?.backgroundColor?.[index] || '',
              }}
            />

            {/* Legend Text */}
            <Typography
              variant="body-sm"
              weight="medium"
              className="!text-grey-800"
            >
              {label}: {data?.values[index]} ({data?.percentages[index]}%)
            </Typography>
          </div>
        ))}
      </div>
    </div>
  );
}
